import { Component, OnInit, OnDestroy } from "@angular/core";
import { UntypedFormGroup, UntypedFormBuilder, Validators } from '@angular/forms';
import { ActivatedRoute, Router } from "@angular/router";
import { TranslateService } from "@ngx-translate/core";
import { SessionContextService } from "projects/packcert-web/src/app/context/session-context.service";
import { Message, SpinnerService } from 'shared-components';
import { EtiquetadoContextService } from "../../context/etiquetado-context.service";
import { EtiquetadoService } from "../../services/etiquetado.service";
import { MobileWatcherService } from "projects/packcert-web/src/app/services/mobile-watcher.service";
import { Subject } from "rxjs";
import { takeUntil } from "rxjs/operators";

@Component({
    selector: 'app-trazabilidad-etiqueta',
    templateUrl: './trazabilidad-etiqueta.component.html'
})
export class TrazabilidadEtiquetaComponent implements OnInit, OnDestroy {

    private readonly _DEFAULT_ITEMS_PER_PAGE = 10;
    private _filters;
    public filtersVisible = true;
    public filtersForm: UntypedFormGroup;

    public infoList;
    public codigoSNC;
    public showOnlyTheReport = false;

    public dropdownSectionFirstLevel = 1;
    public dropdownSectionSecondLevel = 0;

    public mensajes = new Array<Message>();
    public showError: boolean;
    public showWarning: boolean;

    public itemsPerPage = this._DEFAULT_ITEMS_PER_PAGE;
    public currentPage = 1;
    public firstItemList = 0;
    public lastItemList = this.itemsPerPage;
    public totalItems: number;
    public readonly visiblePages = 3;

    public toCloseSpinner = 0;

    // Para los desplegables con datos del blockchain
    private blockchainContainer: HTMLElement;
    private blockchainTitle: HTMLElement;
    private blockchainArrow: HTMLElement;
    private blockchainData: HTMLElement;
    private titleHeight: number;
    private dataHeight: number;

    // Componente stepper
    public steps = ['trazabilidad-etiqueta.informe.cultivo.titulo', 'trazabilidad-etiqueta.informe.designacion.titulo', 'trazabilidad-etiqueta.informe.confeccion.titulo'];
    public icons = ['fas fa-seedling i-cultivo', 'fas fa-handshake i-designacion', 'fas fa-boxes-stacked i-confeccion'];
    public currentStep = 0;
    public fixedSectionsForMobile = false;
    // Desplegables anidados del stepper
    public parentPosition: number;
    public blockchainInfoHeight: number;
    public blockchainAction: string;

    private readonly destroy$ = new Subject<void>();

    constructor(
        private readonly _router: Router,
        private _fb: UntypedFormBuilder,
        private readonly _translate: TranslateService,
        private readonly _activatedRoute: ActivatedRoute,
        private readonly _spinnerService: SpinnerService,
        private readonly _etiquetadoService: EtiquetadoService,
        private readonly _etiquetadoContext: EtiquetadoContextService,
        private readonly _sessionContext: SessionContextService,
        private readonly _mobileWatcher: MobileWatcherService
    ) { }

    ngOnInit() {
        this.filtersForm = this._fb.group({
            codigoSNC: ['', [Validators.required, Validators.minLength(13), Validators.maxLength(13)]]
        });

        this._etiquetadoContext.trazabilidadEtiquetaFilters = null;

        this.checkWarnings(this.filtersForm.get('codigoSNC').value);

        this._activatedRoute.queryParams.subscribe(params => {
            const codeSnc = params.q;
            if (codeSnc) {
                this.filtersForm.get('codigoSNC').patchValue(codeSnc);
                this.mensajes = [];
                this.filtrar();
            }
        });

        this.filtersForm.get('codigoSNC').valueChanges.pipe(takeUntil(this.destroy$)).subscribe((codigoSNC) => {
            if (codigoSNC) {
                if (this.filtersForm.invalid) {
                    this.showError = true;
                } else {
                    this.showError = false;
                }
            } else {
                this.showError = false;
                this.showWarning = false;
            }
        });

        this.fixedSectionsForMobile = this._mobileWatcher.getMobileDevice();
    }

    ngOnDestroy(): void {
        this.destroy$.next();
        this.destroy$.complete();
    }

    public get role() {
        return (this._sessionContext.sessionUser && this._sessionContext.sessionUser) ? this._sessionContext.sessionUser.role : '';
    }

    public get claims() {
        return (this._sessionContext.sessionUser && this._sessionContext.sessionUser.lsClaimTypes) ? this._sessionContext.sessionUser.lsClaimTypes : [];
    }

    public get defaultLanguage() {
        return this._translate.getDefaultLang();
    }

    public get isSmallScreen(){
      return this._sessionContext.isSmallScreen();
    }

    private checkWarnings(codigoSNC) {
        if (codigoSNC && this.filtersForm.valid) {
            this.filtersVisible = false;
            this.showWarning = false;
            this.mensajes = [];
            this.getInfoTrazabilidad();
        } else {
            this.filtersVisible = true;
            this.showWarning = true;
            this.mensajes.push({ class: 'warning', message: 'global.info.filterRequired' });
        }
    }

    public checkValuesForm(): boolean {
        return this.filtersForm.get('codigoSNC').value ? true : false;
    }

    public resetForm() {
        this.filtersForm.reset();
        this.checkWarnings(this.filtersForm.get('codigoSNC').value);
    }

    public filtrar() {
        if (this.filtersForm.invalid) {
            this.showError = true;
            if (this.filtersForm.get('codigoSNC').hasError('required')) {
                this.mensajes.push({ class: 'error', message: 'global.error.required' });
            } else if (this.filtersForm.get('codigoSNC').hasError('minlength') || this.filtersForm.get('codigoSNC').hasError('maxlength')) {
                this.mensajes.push({ class: 'error', message: 'ApiErrors.sncLongitud' });
            }
        } else {
            this.applyFilters();
        }
    }

    public applyFilters() {
        this.currentPage = 1;
        this._etiquetadoContext.trazabilidadEtiquetaFilters = {
            codigoSNC: this.filtersForm.get('codigoSNC').value ? this.filtersForm.get('codigoSNC').value : ''
        };
        this.getInfoTrazabilidad();
    }

    public getInfoTrazabilidad() {
        if (this._etiquetadoContext.trazabilidadEtiquetaFilters) {
            this._filters = this._etiquetadoContext.trazabilidadEtiquetaFilters;
        } else {
            this.applyFilters();
        }
        this._spinnerService.showSpinner();
        this._etiquetadoService.getTrazabilidadEtiqueta(this._filters).toPromise().finally(() => {
            this._spinnerService.hideSpinner();
        }).then((resp) => {
            this.codigoSNC = this.filtersForm.get('codigoSNC').value;
            // Para móvil, una vez está lista la respuesta (y antes de que aparezcan los datos) se muestra solo el informe
            if (this.fixedSectionsForMobile) {
                this.showOnlyTheReport = true;
            }
            this.infoList = resp;
        },
        (error) => {
            this.showError = true;
            if (error.status === 420) {
                switch (error.error.ApiErrorCode) {
                    case 6037:
                        this.mensajes.push({ class: 'error', message: 'ApiErrors.sncNoExiste' });
                        break;
                    default:
                        this.mensajes.push({ class: 'error', message: 'global.error.noResponse' });
                        break;
                }
            } else {
                this.mensajes.push({ class: 'error', message: 'global.error.noResponse' });
            }
        });
    }

    private checkCloseSpinner() {
      this.toCloseSpinner -= 1;
      if (this.toCloseSpinner === 0) {
          this._spinnerService.hideSpinner();
      }
    }

    public openDocHelp() {
      this._spinnerService.showSpinner();
      this.toCloseSpinner += 1;
      this._etiquetadoService.getDocHelp('Labeling_Traceability_Label.pdf').toPromise().finally(() => {
          this.checkCloseSpinner();
      }).then((resp) => {
          localStorage.setItem('base64', resp.base64Archivo);
          const url = this._router.serializeUrl(this._router.createUrlTree(['viewPdf']));
          window.open(url, '_blank');
      });
    }

    onStepChange(step: number) {
        this.currentStep = step;
    }

    /**
     * Expandir o contraer los datos del blockchain
     * 
     * @param parentIndex La posisión del .step-content que se envía al componente stepper
     * @param severalAccordions Para diferenciar entre apartados que necesitan varios desplegables
     * (en el de Cultivo se devuelven varias campañas; cada uno de sus cultivos necesita su propio acordeón)
     * @param accordionSection El apartado en que se encuentra el acordeón: Cultivo, Designación o Confección
     * @param accordionIndex Opcional. Si hay varios desplegables en el apartado, al nombre de la clase le asignamos también su posición (ver ngClass)
     */
    public showOrHideData(parentIndex: number, severalAccordions: boolean, accordionSection: string, accordionIndex?: number, accordionIndex2?: number) {
        switch (severalAccordions) {
            // Si hay más de 1 acordeón en el apartado
            case true:
                this.blockchainContainer = document.getElementsByClassName(`blockchain-container-${accordionSection}${accordionIndex}${accordionIndex2}`)[0] as HTMLElement;
                this.blockchainTitle = document.getElementsByClassName(`blockchain-title-${accordionSection}${accordionIndex}${accordionIndex2}`)[0] as HTMLElement;
                this.blockchainArrow = document.getElementsByClassName(`blockchain-arrow-${accordionSection}${accordionIndex}${accordionIndex2}`)[0] as HTMLElement;
                this.blockchainData = document.getElementsByClassName(`blockchain-content-${accordionSection}${accordionIndex}${accordionIndex2}`)[0] as HTMLElement;
                break;
            // Si solo hay 1 acordeón
            case false:
                this.blockchainContainer = document.getElementsByClassName(`blockchain-container-${accordionSection}`)[0] as HTMLElement;
                this.blockchainTitle = document.getElementsByClassName(`blockchain-title-${accordionSection}`)[0] as HTMLElement;
                this.blockchainArrow = document.getElementsByClassName(`blockchain-arrow-${accordionSection}`)[0] as HTMLElement;
                this.blockchainData = document.getElementsByClassName(`blockchain-content-${accordionSection}`)[0] as HTMLElement;
                break;
        }
        
        if (this.blockchainTitle) {
            this.titleHeight = this.blockchainTitle.offsetHeight;
        }
        if (this.blockchainData) {
            this.dataHeight = this.blockchainData.offsetHeight;
        }

        // Desplegamos los datos del blockchain
        if (this.blockchainContainer.classList.contains('collapsed')) {
            this.blockchainContainer.classList.remove('collapsed');
            this.blockchainArrow.style.transform = 'rotate(-180deg)';
            this.blockchainContainer.style.height = this.titleHeight + this.dataHeight + "px";
            this.blockchainInfoHeight = this.dataHeight;
            this.parentPosition = parentIndex;
            if (severalAccordions) {
                this.blockchainAction = `open${accordionSection}${accordionIndex}${accordionIndex2}`;
            } else if (!severalAccordions) {
                this.blockchainAction = `open${accordionSection}`;
            }

        // Contraemos los datos
        } else if (!this.blockchainContainer.classList.contains('collapsed')) {
            this.blockchainContainer.classList.add('collapsed');
            this.blockchainArrow.style.transform = 'rotate(0)';
            this.blockchainContainer.style.height = this.titleHeight + "px";
            this.parentPosition = parentIndex;
            if (severalAccordions) {
                this.blockchainAction = `close${accordionSection}${accordionIndex}${accordionIndex2}`;
            } else if (!severalAccordions) {
                this.blockchainAction = `close${accordionSection}`;
            }
        }
    }
}
